/**
 * Month-bucket helpers for the Weld Analytics Overview tab.
 *
 * "Current" period = first-of-this-month → `now`.
 * "Previous" period = first-of-last-month → same offset into last month (fair MTD-vs-MTD comparison).
 *
 * All boundaries are returned as Unix-ms; the production weld data uses Unix-seconds in
 * `startTime`/`endTime`, so callers should multiply by 1000 before comparing.
 */

export interface MonthBoundaries {
  /** Inclusive start of the current month (ms). */
  currentStart: number;
  /** Exclusive end of the current period — typically `now` (ms). */
  currentEnd: number;
  /** Inclusive start of the previous month (ms). */
  prevStart: number;
  /** Exclusive end of the previous-month-to-date window (ms). */
  prevEnd: number;
}

export function getMonthBoundaries(now: Date = new Date()): MonthBoundaries {
  const year = now.getFullYear();
  const month = now.getMonth();
  const currentStart = new Date(year, month, 1, 0, 0, 0, 0).getTime();
  const currentEnd = now.getTime();
  const prevStart = new Date(year, month - 1, 1, 0, 0, 0, 0).getTime();

  // Same offset into last month as `now` is into this month — capped at the
  // last instant of last month so a 31-day current month doesn't read past
  // the end of a 30-day previous month.
  const offsetIntoMonth = currentEnd - currentStart;
  const lastDayOfPrevMonth = new Date(year, month, 0, 23, 59, 59, 999).getTime();
  const prevEnd = Math.min(prevStart + offsetIntoMonth, lastDayOfPrevMonth + 1);

  return { currentStart, currentEnd, prevStart, prevEnd };
}

export interface DatedWeld {
  /** Unix-seconds (production store) or Unix-ms — see `unit` arg in `weldsInRange`. */
  startTime?: number | null;
  start_time?: number | null;
}

/**
 * Filter welds whose start falls in `[startMs, endMs)`.
 * `unit` controls how raw `startTime` values are interpreted; production store
 * stores Unix seconds (default), tests can pass `'ms'` for clarity.
 */
export function weldsInRange<T extends DatedWeld>(
  welds: readonly T[],
  startMs: number,
  endMs: number,
  unit: 'seconds' | 'ms' = 'seconds',
): T[] {
  const factor = unit === 'seconds' ? 1000 : 1;
  return welds.filter((w) => {
    const raw = w.startTime ?? w.start_time;
    if (raw == null) return false;
    const ms = Number(raw) * factor;
    if (!Number.isFinite(ms)) return false;
    return ms >= startMs && ms < endMs;
  });
}

export interface PassFailCounts {
  passed: number;
  failed: number;
  other: number;
}

/**
 * Bucket welds into pass / fail / other using the same status resolver the
 * production WeldDataMainTable uses, so analytics and production agree.
 *
 * The resolver receives the raw weld row and returns the raw status string
 * (e.g. 'TRUE' / 'FALSE' / 'NULL'). Pass-through is intentional — callers
 * can use `getWeldStatusCalculationMethod(method, weld)` from `src/utils/statusUtils.ts`.
 */
export function passFailCounts<T>(welds: readonly T[], getStatus: (weld: T) => unknown): PassFailCounts {
  let passed = 0;
  let failed = 0;
  let other = 0;
  welds.forEach((w) => {
    const raw = getStatus(w);
    const s = raw == null ? '' : String(raw).toUpperCase();
    if (s === 'TRUE' || s === 'COMPLETED' || s === 'PASS' || s === 'PASSED' || s === '1') {
      passed += 1;
    } else if (s === 'FALSE' || s === 'REJECTED' || s === 'FAIL' || s === 'FAILED' || s === '0' || s === 'CROSS') {
      failed += 1;
    } else {
      other += 1;
    }
  });
  return { passed, failed, other };
}

/**
 * Average weld duration in seconds across the provided welds.
 * Accepts `weld_time` (number in minutes, production data shape) or a parsed
 * `weldTime` string like "2.56 mins". Returns `null` when no welds have a duration.
 */
export function avgDurationSec<T extends { weld_time?: number | null; weldTime?: number | string | null }>(
  welds: readonly T[],
): number | null {
  let totalMinutes = 0;
  let count = 0;
  welds.forEach((w) => {
    const fromNumeric = typeof w.weld_time === 'number' && Number.isFinite(w.weld_time) ? w.weld_time : null;
    if (fromNumeric != null) {
      totalMinutes += fromNumeric;
      count += 1;
      return;
    }
    if (w.weldTime != null) {
      if (typeof w.weldTime === 'number' && Number.isFinite(w.weldTime)) {
        totalMinutes += w.weldTime;
        count += 1;
        return;
      }
      const match = String(w.weldTime).match(/(-?\d+\.?\d*)/);
      if (match) {
        const n = parseFloat(match[1]);
        if (Number.isFinite(n)) {
          totalMinutes += n;
          count += 1;
        }
      }
    }
  });
  if (count === 0) return null;
  // weld_time is minutes in the production schema; the OverviewTab callers
  // convert this to a human label, so we expose seconds for consistency with
  // formatDuration which accepts minutes too.
  return (totalMinutes / count) * 60;
}

export type DeltaDirection = 'up' | 'down' | 'flat';

export interface Delta {
  /** Percentage change from `prev` to `current`. Null when prev is null/0. */
  pct: number | null;
  /** Absolute change (current - prev). */
  abs: number;
  direction: DeltaDirection;
}

/**
 * Compute the delta between current and previous KPI values.
 * - `pct = null` when prev is null, undefined, or zero (avoids divide-by-zero noise).
 * - `direction === 'flat'` when both values are equal.
 */
export function deltaPct(current: number | null | undefined, prev: number | null | undefined): Delta {
  const c = current == null ? 0 : current;
  const p = prev == null ? 0 : prev;
  const abs = c - p;
  let direction: DeltaDirection;
  if (abs > 0) direction = 'up';
  else if (abs < 0) direction = 'down';
  else direction = 'flat';

  if (prev == null || prev === 0) {
    return { pct: null, abs, direction };
  }
  return { pct: (abs / Math.abs(prev)) * 100, abs, direction };
}
